/**
 * League Squares room client — members-only grids for the match-play UI.
 * Used by `squaresApi` when League mode is active so public/bot rooms never load.
 */

import { getAuthHeaders } from "../../../utils/authHeaders";
import { LEAGUE_ID_QUERY } from "./leagueGamePlayUrl";
import { peekActiveLeagueId } from "./leagueModeStore";
import { isPersistedLeagueId } from "./persistedLeagueId";
import { findLeague } from "./leagueStore";
import { LEAGUE_VIEWER_USER_ID } from "./constants";
import {
    buildPrototypeLeagueSquaresRoom,
    type LeagueSquaresRoomPayload,
} from "./leagueSquaresPrototypeRoom";

export { buildPrototypeLeagueSquaresRoom } from "./leagueSquaresPrototypeRoom";
export type { LeagueSquaresRoomPayload } from "./leagueSquaresPrototypeRoom";

const EMPTY_MATCH: LeagueSquaresRoomPayload = {
    plays: [],
    overs: {},
    overOddsLocked: {},
    over_economy: {},
    myOverNumbers: [],
    top_prize: 0,
};

function apiBase(): string {
    return (process.env.API_URL || "").replace(/\/$/, "");
}

/** Active league id for Squares room isolation (mode store or `?leagueId=`). */
export function resolveActiveLeagueIdForSquaresRoom(): string | null {
    const fromMode = peekActiveLeagueId();
    if (fromMode) return fromMode;
    if (typeof window === "undefined") return null;
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(LEAGUE_ID_QUERY);
        const trimmed = fromUrl?.trim();
        return trimmed || null;
    } catch {
        return null;
    }
}

/**
 * Fetch the league-only Squares room for a match/innings (optionally one over).
 * Returns null when not in league mode (and no `leagueId` override) so callers can fall through
 * to public Squares APIs.
 */
export async function fetchLeagueSquaresRoomIfActive(
    searchParams: URLSearchParams | Record<string, string>,
    options?: { leagueId?: string | null },
): Promise<LeagueSquaresRoomPayload | null> {
    const leagueId = String(options?.leagueId || "").trim() || resolveActiveLeagueIdForSquaresRoom();
    if (!leagueId) return null;

    const q = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
    const matchId = q.get("matchId") ?? q.get("match_id") ?? "";
    if (!matchId) return { ...EMPTY_MATCH };

    const proto = findLeague(leagueId);
    if (proto) {
        return buildPrototypeLeagueSquaresRoom(proto, q, LEAGUE_VIEWER_USER_ID);
    }
    // Lobby chrome (e.g. "lobby-finished") is a local board, not a row in `leagues`.
    if (!isPersistedLeagueId(leagueId)) return { ...EMPTY_MATCH };

    const params = new URLSearchParams({ matchId: String(matchId) });
    const innings = q.get("inningsNumber") ?? q.get("innings_number");
    if (innings != null && String(innings).trim() !== "") params.set("inningsNumber", String(innings));
    const overNumber = q.get("overNumber") ?? q.get("over_number");
    if (overNumber != null && String(overNumber).trim() !== "") params.set("overNumber", String(overNumber));
    const currentOverNumber = q.get("currentOverNumber") ?? q.get("current_over_number");
    if (currentOverNumber != null && String(currentOverNumber).trim() !== "") {
        params.set("currentOverNumber", String(currentOverNumber));
    }
    const ballResults = q.get("ballResults") ?? q.get("ball_results");
    if (ballResults != null && String(ballResults).trim() !== "") params.set("ballResults", String(ballResults));

    const url = `${apiBase()}/leagues/${encodeURIComponent(leagueId)}/squares-plays?${params.toString()}`;
    try {
        const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
        if (!res.ok) return { ...EMPTY_MATCH };
        const data = await res.json();
        return {
            plays: Array.isArray(data?.plays) ? data.plays : [],
            overs: data?.overs && typeof data.overs === "object" ? data.overs : {},
            overOddsLocked:
                data?.overOddsLocked && typeof data.overOddsLocked === "object" ? data.overOddsLocked : {},
            over_economy: data?.over_economy && typeof data.over_economy === "object" ? data.over_economy : {},
            myOverNumbers: Array.isArray(data?.myOverNumbers)
                ? data.myOverNumbers.map((n: unknown) => Number(n)).filter((n: number) => Number.isFinite(n))
                : [],
            min_players: data?.min_players,
            pool_entrant_count: data?.pool_entrant_count,
            top_prize: data?.top_prize ?? 0,
            ...(Array.isArray(data?.ball_results) ? { ball_results: data.ball_results } : {}),
        };
    } catch {
        return { ...EMPTY_MATCH };
    }
}

/**
 * Viewer's overs in the league Squares room for this match/innings.
 * Returns null when not in league mode.
 */
export async function fetchLeagueSquaresMyOversIfActive(
    matchId: string,
    inningsNumber: number,
): Promise<{ overNumbers: number[] } | null> {
    const leagueId = resolveActiveLeagueIdForSquaresRoom();
    if (!leagueId) return null;

    const data = await fetchLeagueSquaresRoomIfActive({
        matchId: String(matchId),
        inningsNumber: String(inningsNumber),
    });
    if (!data) return null;
    return { overNumbers: data.myOverNumbers || [] };
}
